import React, { useCallback, useEffect, useRef } from 'react'
import ApexCharts from 'apexcharts'
import { useThemeMode } from '../../../_metronic/partials'
import { getCSSVariableValue } from '../../../_metronic/assets/ts/_utils'
import { montarOpcoesSerieDiaria, PontoSerie, rotuloDoPonto } from './serieDiariaOpcoes'

type Props = {
  /** `null` quando o indicador não tem série (ex.: SLA não configurado). */
  pontos: PontoSerie[] | null
  nomeSerie: string
  formatarValor: (valor: number) => string
  formatarEixoY: (valor: number) => string
  formatarRotuloDia: (data: string) => string
  /** Rótulo de ponto agrupado ("dd/MM a dd/MM"), no tooltip e na tabela. */
  formatarIntervalo?: (dataInicio: string, dataFim: string) => string
  /** Texto do estado sem série; quem chama decide qual (sem dados, SLA não configurado). */
  textoSemDados: string
  maximoY?: number
  /** Média do período anterior, desenhada como linha tracejada. */
  valorReferencia?: number | null
  /** Legenda e cabeçalhos da tabela textual que acompanha o gráfico. */
  tabela?: { legenda: string; colunaData: string; colunaValor: string; colunaN: string }
}

/** Série em linha, com lacuna nos pontos sem dado, o último ponto rotulado e a referência tracejada. */
const SerieDiariaChart: React.FC<Props> = ({
  pontos,
  nomeSerie,
  formatarValor,
  formatarEixoY,
  formatarRotuloDia,
  formatarIntervalo,
  textoSemDados,
  maximoY,
  valorReferencia,
  tabela,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()
  const temDado = Boolean(pontos?.some((ponto) => ponto.valor !== null))

  const refreshChart = useCallback(() => {
    if (!chartRef.current || !pontos || !temDado) return

    const chart = new ApexCharts(
      chartRef.current,
      montarOpcoesSerieDiaria({
        pontos,
        nomeSerie,
        cores: {
          serie: getCSSVariableValue('--app-chart-series'),
          texto: getCSSVariableValue('--bs-gray-700'),
          grade: getCSSVariableValue('--bs-gray-300'),
          fundoRotulo: getCSSVariableValue('--bs-body-bg'),
          referencia: getCSSVariableValue('--bs-gray-700'),
        },
        formatarValor,
        formatarEixoY,
        formatarRotuloDia,
        formatarIntervalo,
        textoSemDados,
        maximoY,
        valorReferencia,
        larguraGrafico: chartRef.current.clientWidth || undefined,
      })
    )

    chart.render()

    return chart
  }, [
    pontos,
    temDado,
    nomeSerie,
    formatarValor,
    formatarEixoY,
    formatarRotuloDia,
    formatarIntervalo,
    textoSemDados,
    maximoY,
    valorReferencia,
  ])

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [mode, refreshChart])

  if (!pontos || !temDado) {
    return (
      <p className='d-flex align-items-center text-gray-700 fs-6 mb-0 py-10 justify-content-center'>
        <i className='bi bi-dash-circle me-2 fs-5' aria-hidden='true' />
        <span>{textoSemDados}</span>
      </p>
    )
  }

  return (
    <>
      <div ref={chartRef} aria-hidden='true' />
      {tabela && (
        <div className='visually-hidden'>
          <table>
            <caption>{tabela.legenda}</caption>
            <thead>
              <tr>
                <th scope='col'>{tabela.colunaData}</th>
                <th scope='col'>{tabela.colunaValor}</th>
                <th scope='col'>{tabela.colunaN}</th>
              </tr>
            </thead>
            <tbody>
              {pontos.map((ponto) => (
                <tr key={ponto.data}>
                  <td>{rotuloDoPonto(ponto, formatarRotuloDia, formatarIntervalo)}</td>
                  <td>{ponto.valor === null ? textoSemDados : formatarValor(ponto.valor)}</td>
                  <td>{ponto.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

export { SerieDiariaChart }
